import React, { useCallback, useEffect, useState, useMemo } from 'react'
import Image from 'next/legacy/image'
import { useUserId, useAccessToken, useElevateSecurityKeyEmail, useAddSecurityKey } from '@nhost/nextjs'
import cx from 'classnames'
import axios from 'axios'
import { Dialog, Transition } from '@headlessui/react'
import to from 'await-to-js'
import { backOff } from 'exponential-backoff'
import Web3 from 'web3'
import { ethers } from 'ethers'
import Web3Modal from 'web3modal'
import { useUserAgent } from 'hooks'

import { Button, Text } from 'components/atomic'
import TransferIcon from 'components/icon/transfer'
import EditIcon from '../../../../public/images/onboarding/icon-edit.svg'

import { isProduction } from 'lib/constants'
import { initProviderOptions } from 'utils/providerOptions'
import { callTransfer } from 'utils/web3'
import getWalletTokens from 'utils/getWalletTokens'

import {
  useUserMetadataQuery,
  useUserWalletMutation,
  usePfpBoughtByUserAndPaymentQuery,
  useSecurityKeyNameUpdateMutation,
} from 'generated/graphql'

import styles from './index.module.scss'

const log = (msg: string, error = Error()) => {
  let err = error.toString()
  console.log(`${msg}: ${err} | error:`, error)
  if (!err || err === 'Error') alert(msg)
  else if (`${err}`.indexOf('[object Object]') === -1) {
    if (err.toLowerCase() === 'modal closed by user') {
      err = 'Install web3 browser extension to connect your wallet'
    }
    alert(`${msg}: ${err}`)
  }
}

let web3Modal
// prevent init on server side
if (typeof window !== 'undefined') {
  web3Modal = new Web3Modal({
    // network: 'mainnet',
    // chainId: '100',
    cacheProvider: false,
    providerOptions: initProviderOptions(),
  })
}

function initWeb3(provider) {
  const web3 = new Web3(provider)

  web3.eth.extend({
    methods: [
      {
        name: 'chainId',
        call: 'eth_chainId',
        // outputFormatter: web3.utils.hexToNumber,
      },
    ],
  })

  return web3
}

type AccessPassToken = {
  wallet: string
  onchain_tokenId: number
}
type ClawsToken = {
  wallet: string
  tokenId: number
}
type StardustTokenId = {
  onchain_tokenId: number | undefined | null
  stardust_tokenId: number | undefined | null
}
type NFT = {
  name: string
  currentWallet: string
  transferAvailable: boolean
  tokenId: number
}
interface WalletConnectionProps {
  loadingStardustWallet: boolean
  isSmallScreen?: boolean
}

export default function WalletConnection({ loadingStardustWallet, isSmallScreen }: WalletConnectionProps) {
  const userId = useUserId() as string
  const [web3, setWeb3] = useState<Web3>()
  const [chainId, setChainId] = useState<number>(137)
  // const [provider, setProvider] = useState()
  // const [library, setLibrary] = useState<ethers.providers.Web3Provider>()

  const nftsFounderQuery = usePfpBoughtByUserAndPaymentQuery({
    variables: {
      userId,
    },
  })
  const { loading, data, error, refetch: refetchUser } = useUserMetadataQuery()
  const [updateUserWallet] = useUserWalletMutation()
  const [updateNickName] = useSecurityKeyNameUpdateMutation()

  const [isExternalWalletConnected, setIsExternalWalletConnected] = useState(false)
  const [isMSWalletConnected, setIsMSWalletConnected] = useState(false)
  const [externalWalletAddr, setExternalWalletAddr] = useState('')
  const [midnightSocietyWalletAddr, setMidnightSocietyWalletAddr] = useState('')
  const [stardustPlayerId, setStardustPlayerId] = useState('')

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState({ isOpen: false, currentWallet: '' })
  const [confirmNFT, setConfirmNFT] = useState<NFT>()

  const [isTokensLoading, setIsTokensLoading] = useState(false)
  const [isTransferLoading, setIsTransferLoading] = useState(false)
  const [isMSWalletLoading, setIsMSWalletLoading] = useState(false)
  const [isExternalWalletLoading, setIsExternalWalletLoading] = useState(false)

  const [accessPassTokens, setAccessPassTokens] = useState<AccessPassToken[]>([])
  const [clawsTokens, setClawsTokens] = useState<ClawsToken[]>([])
  const [securityKeyApproved, setSecurityKeyApproved] = useState(false)
  const [securityKeyAvailable, setSecurityKeyAvailable] = useState(false)
  const [hasRevokeAccess, setHasRevokeAccess] = useState(false)

  const { elevateEmailSecurityKey } = useElevateSecurityKeyEmail()
  const { add } = useAddSecurityKey()
  const { loading: userDataLoading, data: userData } = useUserMetadataQuery()
  const userEmail = userData?.users_metadata[0]?.user.email
  useEffect(() => {
    if (!userDataLoading) {
      setSecurityKeyAvailable(!!userData?.users_metadata[0]?.user.securityKeys[0]?.nickname)
    }
  }, [userDataLoading, userData])

  const device = useUserAgent()?.device as UAParser.IDevice

  const accessToken = useAccessToken()
  const handleAddKey = async (e) => {
    e.preventDefault()
    try {
      if (securityKeyAvailable) {
        const { elevated, isError: isElevateError, error: elevateError } = await elevateEmailSecurityKey(userEmail)
        if (isElevateError) {
          console.log(JSON.stringify({ message: `isElevateError: ${isElevateError}`, error: elevateError }))
          setSecurityKeyApproved(false)
        } else if (elevated) {
          const nickName = data?.users_metadata[0]?.user.securityKeys[0]?.nickname
          const nickNameSplitted = nickName?.split(' | ')
          if (!nickNameSplitted?.includes('elevated')) {
            // NOTE: suffix ' | elevated' means the passkey has been used for elevated access at least once
            const newNickName = nickName?.concat(' | elevated') as string
            try {
              await updateNickName({
                variables: {
                  userId,
                  nickName: newNickName,
                },
              })
            } catch (err) {
              console.error(`Failed to update the security key nickName | ${err}`)
            }
          }
          setSecurityKeyApproved(true)
        }
      } else {
        const { error: addKeyError, key } = await add(
          `${device.type ?? '-'} | ${device.vendor ?? '-'} | ${device.model ?? '-'}`,
        )
        if (addKeyError) {
          console.log(JSON.stringify({ message: `Add key error`, error: addKeyError }))
          setSecurityKeyApproved(false)
        } else {
          setSecurityKeyApproved(true)
          setSecurityKeyAvailable(true)
        }
      }
    } catch (err) {
      console.log(JSON.stringify({ message: `Error while adding`, error: err }))
      setSecurityKeyApproved(false)
    }
  }
  const handleRevokeKey = async (e) => {
    e.preventDefault()
    const { elevated, isError: isElevateError, error: elevateError } = await elevateEmailSecurityKey(userEmail)
    if (isElevateError) {
      console.log(JSON.stringify({ message: `isElevateError: ${isElevateError}`, error: elevateError }))
    } else if (elevated) {
      setHasRevokeAccess(true)
    }
  }

  useEffect(() => {
    const revokeKey = async () => {
      if (hasRevokeAccess) {
        const options = {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'content-type': 'application/json',
          },
        }

        const response = await fetch(`/api/revoke-security-key?&accessToken=${accessToken}`, options)
        if (response.status === 200) {
          setSecurityKeyAvailable(false)
          setHasRevokeAccess(false)
        } else {
          const body = response.text()
          console.log(`Security key revoke error ${body}`)
        }
      }
    }
    revokeKey()
  }, [hasRevokeAccess, accessToken])

  useEffect(() => {
    if (!loadingStardustWallet) refetchUser()
  }, [loadingStardustWallet, refetchUser])

  useEffect(() => {
    const response = data?.users_metadata[0]

    if (response) {
      setExternalWalletAddr(response?.wallet_self_custody || '')
      setMidnightSocietyWalletAddr(response?.wallet_stardust || '')

      if (response?.stardust_playerId) {
        setStardustPlayerId(response.stardust_playerId)
      }
      if (response?.wallet_stardust?.length === 42) {
        setIsMSWalletConnected(true)
      }
      // if (response?.wallet_self_custody?.length === 42) {
      // setIsExternalWalletConnected(true)
      // }
    }

    if (isExternalWalletConnected) refetchUser()
  }, [data, isExternalWalletConnected, refetchUser])

  // useEffect for checking wallets at initial and after NFT transfer finished
  useEffect(() => {
    const response = data?.users_metadata[0]
    if (response) {
      const getTokensFromWallets = async () => {
        setIsTokensLoading(true)
        const tempAccessPassTokens: any[] = []
        const tempClawsTokens: any[] = []

        if (midnightSocietyWalletAddr) {
          try {
            const accessPassRes = await getWalletTokens(midnightSocietyWalletAddr)
            if (accessPassRes.token_found)
              tempAccessPassTokens.push(
                ...accessPassRes.token_ids.map((item) => ({ wallet: 'mswallet', onchain_tokenId: item })),
              )

            const clawsRes = await getWalletTokens(midnightSocietyWalletAddr, process.env.NEXT_PUBLIC_CONTRACT_CLAWS)
            if (clawsRes.token_found)
              tempClawsTokens.push(...clawsRes.token_ids.map((item) => ({ wallet: 'mswallet', tokenId: item })))
          } catch (err) {
            console.log('Error during getting tokens from MS Wallet', err)
          }
        }

        if (externalWalletAddr) {
          try {
            const accessPassRes = await getWalletTokens(externalWalletAddr)
            if (accessPassRes.token_found)
              tempAccessPassTokens.push(
                ...accessPassRes.token_ids.map((item) => ({ wallet: 'externalwallet', onchain_tokenId: item })),
              )

            const clawsRes = await getWalletTokens(externalWalletAddr, process.env.NEXT_PUBLIC_CONTRACT_CLAWS)
            if (clawsRes.token_found)
              tempClawsTokens.push(...clawsRes.token_ids.map((item) => ({ wallet: 'externalwallet', tokenId: item })))
          } catch (err) {
            console.log('Error during getting tokens from External Wallet', err)
          }
        }

        setAccessPassTokens(tempAccessPassTokens)
        setClawsTokens(tempClawsTokens)
        setIsTokensLoading(false)
      }

      if (!isMSWalletLoading && !isExternalWalletLoading) getTokensFromWallets()
    }
  }, [data?.users_metadata, midnightSocietyWalletAddr, externalWalletAddr, isMSWalletLoading, isExternalWalletLoading])

  /* https://www.npmjs.com/package/exponential-backoff options
  retry?: (e: any, attemptNumber: number) => boolean | Promise<boolean>

  The retry function can be used to run logic after every failed attempt (e.g. logging a message, assessing the last error, etc.). It is called with the last error and the upcoming attempt number. Returning true will retry the function as long as the numOfAttempts has not been exceeded. Returning false will end the execution.

  Default value is a function that always returns true.
  */
  const retry = async (fn) =>
    await backOff(() => fn(), { startingDelay: 7000, timeMultiple: 1.5, delayFirstAttempt: true, numOfAttempts: 20 })

  // useEffect for NFT transfer from External Wallet to MS Wallet
  useEffect(() => {
    const response = data?.users_metadata[0]
    if (response && confirmNFT) {
      const checkMSWallet = async () => {
        try {
          if (confirmNFT.name.toLowerCase().includes('access pass')) {
            retry(async () => {
              const accessPassResponse = await getWalletTokens(midnightSocietyWalletAddr)
              if (accessPassResponse.token_found && accessPassResponse.token_ids.includes(confirmNFT.tokenId)) {
                setIsExternalWalletLoading(false)
              } else throw 'retry'
            })
          } else if (confirmNFT.name === 'Series 0 patch') {
            retry(async () => {
              const clawsResponse = await getWalletTokens(
                midnightSocietyWalletAddr,
                process.env.NEXT_PUBLIC_CONTRACT_CLAWS,
              )
              if (clawsResponse.token_found && clawsResponse.token_ids.includes(confirmNFT.tokenId)) {
                setIsExternalWalletLoading(false)
              } else throw 'retry'
            })
          }
        } catch {
          setIsExternalWalletLoading(false)
        }
      }

      if (midnightSocietyWalletAddr && isExternalWalletLoading) checkMSWallet()
    }
  }, [data?.users_metadata, midnightSocietyWalletAddr, isExternalWalletLoading, confirmNFT])

  // useEffect for NFT transfer from MS Wallet to External Wallet
  useEffect(() => {
    const response = data?.users_metadata[0]
    if (confirmNFT && response) {
      const checkExternalWallet = async () => {
        try {
          if (confirmNFT.name.toLowerCase().includes('access pass')) {
            retry(async () => {
              const accessPassResponse = await getWalletTokens(externalWalletAddr)
              if (accessPassResponse.token_found && accessPassResponse.token_ids.includes(confirmNFT.tokenId)) {
                setIsMSWalletLoading(false)
              } else throw 'retry'
            })
          } else if (confirmNFT.name === 'Series 0 patch') {
            retry(async () => {
              const clawsResponse = await getWalletTokens(externalWalletAddr, process.env.NEXT_PUBLIC_CONTRACT_CLAWS)
              if (clawsResponse.token_found && clawsResponse.token_ids.includes(confirmNFT.tokenId)) {
                setIsMSWalletLoading(false)
              } else throw 'retry'
            })
          }
        } catch (err) {
          setIsMSWalletLoading(false)
        }
      }

      if (externalWalletAddr && isMSWalletLoading) checkExternalWallet()
    }
  }, [data?.users_metadata, externalWalletAddr, isMSWalletLoading, confirmNFT])

  const stardustTokenIds = useMemo(() => {
    const tokenIds: StardustTokenId[] = []
    if (!nftsFounderQuery.loading && nftsFounderQuery.data?.nfts_founder) {
      nftsFounderQuery.data.nfts_founder.forEach((item) =>
        tokenIds.push({ onchain_tokenId: item.onchain_tokenId, stardust_tokenId: item.stardust_tokenId }),
      )
    }
    if (!loading && data?.clawNfts) {
      data.clawNfts.forEach((item) =>
        tokenIds.push({ onchain_tokenId: item.onchain_tokenId, stardust_tokenId: item.stardust_tokenId }),
      )
    }
    return tokenIds
  }, [nftsFounderQuery, data, loading])

  const checkTransferAvailable = useCallback(
    (currentWallet, isMSWalletConnected, isExternalWalletConnected, onchain_tokenId = 0) => {
      if (currentWallet === 'mswallet') {
        // console.log('checkTransferAvailable nftsFounderQuery.data?.nfts_founder:', nftsFounderQuery.data?.nfts_founder)
        if (nftsFounderQuery.data?.nfts_founder && nftsFounderQuery.data.nfts_founder.length > 0) {
          let result = false
          nftsFounderQuery.data.nfts_founder.forEach((founder) => {
            if (founder.onchain_tokenId === onchain_tokenId && !founder.flags.noWithdrawal) result = true
          })
          return result && isExternalWalletConnected
        } else {
          return isExternalWalletConnected ? true : false
        }
      }
      if (currentWallet === 'externalwallet') {
        return isMSWalletConnected ? true : false
      }
    },
    [nftsFounderQuery.data?.nfts_founder],
  )

  const nfts = useMemo(() => {
    let nfts: any[] = []

    accessPassTokens.forEach((item) =>
      nfts.push({
        name: `Access Pass ${item.onchain_tokenId}`,
        currentWallet: item.wallet,
        transferAvailable: checkTransferAvailable(
          item.wallet,
          isMSWalletConnected,
          isExternalWalletConnected,
          item.onchain_tokenId,
        ),
        tokenId: item.onchain_tokenId,
      }),
    )
    clawsTokens.forEach((item) =>
      nfts.push({
        name: 'Series 0 patch',
        currentWallet: item.wallet,
        transferAvailable: checkTransferAvailable(item.wallet, isMSWalletConnected, isExternalWalletConnected),
        tokenId: item.tokenId,
      }),
    )

    return nfts
  }, [accessPassTokens, checkTransferAvailable, clawsTokens, isExternalWalletConnected, isMSWalletConnected])

  const connectWallet = useCallback(async () => {
    await disconnectWallet()
    if (!web3Modal) return

    const [err, provider] = await to<any, any>(web3Modal.connect()) // web3Modal.connectTo('metamask')
    if (err || !provider) {
      if (!err) return console.log(`Please connect your web3 wallet`)
      else if (
        err === 'Modal closed by user' ||
        err?.message === 'User closed modal' ||
        err?.message === 'User denied account authorization'
      ) {
        return
      } else {
        log(`Error connecting your web3 wallet`, err)
        return
      }
    }

    // We plug the initial `provider` into ethers.js and get back
    // a Web3Provider. This will add on methods from ethers.js and
    // event listeners such as `.on()` will be different.
    const library = new ethers.providers.Web3Provider(provider, 'any')

    if (library?.connection?.url) {
      try {
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: '0x89' }], // Polygon Mainnet: 137 - 0x89
          // 100 - 0x64
        })
      } catch (err) {
        console.log(`Wallet change error: ${err}`)
        try {
          await provider.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: '0x89',
                chainName: 'Polygon',
                nativeCurrency: {
                  name: 'POL',
                  symbol: 'POL',
                  decimals: 18,
                },
                rpcUrls: ['https://polygon-rpc.com'], // https://rpc.ankr.com/polygon
                blockExplorerUrls: ['https://polygonscan.com/'],
                // iconUrls: [''],
              },
            ],
          })
        } catch (addError) {
          console.log('Did not add network. Error:', addError)
        }
      }
    }

    const accounts = await library.listAccounts()
    if (accounts && accounts?.[0]?.length === 42) {
      try {
        await updateUserWallet({
          variables: {
            userId,
            wallet_self_custody: accounts[0],
          },
        })
        // console.log(`Connected to wallet address ${accounts[0]}`)
        setIsExternalWalletConnected(true)
        setExternalWalletAddr(accounts[0])
      } catch (error) {
        log(
          'Error connecting your Web3 wallet to your account.' +
            (error.toString().slice(0, 54) === 'ApolloError: Uniqueness violation. duplicate key value'
              ? ' You cannot have multiple Midnight Society accounts connected to the same wallet.'
              : ''),
          // error,
        )
      }
    } else {
      console.error("Can't get accounts:", accounts)
    }
    const web3 = initWeb3(provider)
    const network = await library.getNetwork()
    setChainId(network.chainId ?? 137)
    setWeb3(web3)
    // setProvider(provider)
    // setLibrary(library)
  }, [updateUserWallet, userId])

  const disconnectWallet = async () => {
    await web3Modal.clearCachedProvider()
    setIsExternalWalletConnected(false)
  }

  // const { add, isSuccess, isError } = useAddSecurity()

  const handleTransfer = async () => {
    // await add('')
    setIsTransferLoading(true)

    if (confirmNFT) {
      if (confirmNFT.currentWallet === 'mswallet') {
        try {
          const stardust_tokenId = confirmNFT.name.toLowerCase().includes('access pass')
            ? stardustTokenIds.filter((item) => item.onchain_tokenId === confirmNFT.tokenId)[0]?.stardust_tokenId
            : confirmNFT.tokenId

          if (stardust_tokenId) {
            await axios.get(
              `/api/withdrawToExternalWallet?address=${externalWalletAddr}&stardust_tokenId=${stardust_tokenId}&stardust_playerId=${stardustPlayerId}&accessToken=${accessToken}`,
            )
            setIsMSWalletLoading(true)
          }
        } catch (err) {
          setIsMSWalletLoading(false)
          console.log('err:', err)
          log(`Error with MS Wallet`, err.message)
        } finally {
          setIsConfirmModalOpen({ isOpen: false, currentWallet: '' })
          setIsTransferLoading(false)
        }
      } else if (confirmNFT.currentWallet === 'externalwallet') {
        try {
          if (!web3Modal || !web3) {
            setIsConfirmModalOpen({ isOpen: false, currentWallet: '' })
            setIsTransferLoading(false)
            log(`Please connect your web3 wallet`)
            return
          }
          const nftType = confirmNFT.name.toLowerCase().includes('access pass')
            ? isProduction
              ? 'accessPass'
              : 'testPass'
            : 'claws'
          await callTransfer(nftType, externalWalletAddr, midnightSocietyWalletAddr, confirmNFT.tokenId, chainId, web3)
          setIsExternalWalletLoading(true)
        } catch (err) {
          setIsExternalWalletLoading(false)
          console.log('err:', err)
          log(`Error connecting your web3 wallet`, err.message)
        } finally {
          setIsConfirmModalOpen({ isOpen: false, currentWallet: '' })
          setIsTransferLoading(false)
        }
      }
    }
  }

  const handleOpenConfirmModal = async (nft) => {
    const options = {
      method: 'GET',
      headers: {
        'accept': 'application/json',
        'content-type': 'application/json',
      },
    }
    if (nft.transferAvailable) {
      if (!isConfirmModalOpen.isOpen) {
        setConfirmNFT(nft)
      }

      setIsConfirmModalOpen((prev) => ({ ...prev, isOpen: true, currentWallet: nft.currentWallet }))
    }
  }

  if (loading || isTokensLoading || loadingStardustWallet) {
    return <div className="w-full p-12 text-center">Loading...</div>
  }

  if (error) {
    console.warn('users_metadata query error:', error.message)
  }

  return (
    <>
      <div
        className={`flex flex-col items-center justify-center ${
          isSmallScreen ? '2lg:mb-0 2lg:mr-12 2lg:justify-start' : 'md:mb-0 md:mr-12 md:justify-start'
        } mb-8`}
      >
        <div
          className={cx(
            'relative flex w-[280px] flex-col items-center justify-center',
            isMSWalletConnected ? styles.wallet_connected : styles.connect_wallet,
          )}
        >
          <div className="relative h-[62px] w-[204px]">
            <Image
              src={isMSWalletConnected ? '/images/logo-black.png' : '/images/logo-white.png'}
              layout="fill"
              objectFit="cover"
              alt="Logo"
            />
          </div>
          <div className="ml-8 mt-12 self-start">
            {isMSWalletConnected && <div className={styles.connect_text}>wallet</div>}
            {midnightSocietyWalletAddr.length === 42 && (
              <Text variant="p2" tag="p">
                <a
                  href={`https://polygonscan.com/address/${midnightSocietyWalletAddr}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {midnightSocietyWalletAddr.slice(0, 8)}...{midnightSocietyWalletAddr.slice(-6)}
                </a>
              </Text>
            )}
          </div>
        </div>
        <div
          className={`mb-8 mt-10 flex h-[40px] items-center justify-center font-tactic-extended text-button-lg font-normal uppercase ${
            !isMSWalletConnected && 'opacity-50'
          }`}
        >
          Midnight Society Wallet
        </div>

        {nfts.length > 0 ? (
          nfts.map((nft) => (
            <div
              key={`Token ${nft.tokenId}`}
              className={`flex h-[50px] w-full items-center border px-6 ${
                nft.currentWallet !== 'mswallet' && 'bg-[#151515]'
              }`}
            >
              {nft.currentWallet === 'mswallet' && (
                <div className="flex w-full items-center justify-between">
                  <div className="mr-2 font-tactic-extended text-button-lg font-normal uppercase">{nft.name}</div>
                  <div
                    className={`${nft.transferAvailable ? 'cursor-pointer' : 'opacity-25'}`}
                    onClick={() => handleOpenConfirmModal(nft)}
                  >
                    {isMSWalletLoading && confirmNFT?.name === nft.name ? (
                      <div className={styles.loader} />
                    ) : (
                      <TransferIcon />
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="flex h-[50px] w-full items-center border bg-[#151515] px-6">
            <div className="font-tactic-extended text-button-lg font-normal uppercase opacity-50">
              no items in wallet
            </div>
          </div>
        )}

        {!securityKeyAvailable && (
          <Text variant="caption" className="mt-6">
            <a href="#" onClick={handleAddKey} className="text-secondary hover:underline">
              Add Passkey
            </a>
            , prevent unauthorized withdrawal
          </Text>
        )}
      </div>

      <div className="flex flex-col items-center justify-center md:justify-start">
        <div
          className={cx(
            'relative flex w-[280px] flex-col items-center justify-center',
            isExternalWalletConnected ? styles.wallet_connected : styles.connect_wallet,
          )}
        >
          {isExternalWalletConnected && (
            <div
              className="absolute right-[13px] top-[9px] flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-black"
              onClick={disconnectWallet}
            >
              <EditIcon />
            </div>
          )}
          {!isExternalWalletConnected ? (
            <Button variant="secondary" onClick={connectWallet}>
              Connect Wallet
            </Button>
          ) : (
            <>
              <div className="relative mb-7 h-[126px] w-[126px]">
                <Image src="/images/application/MetaMask_Fox.png" layout="fill" objectFit="cover" alt="Logo" />
              </div>
              <div className={styles.connect_text}>wallet connected</div>
            </>
          )}

          {externalWalletAddr.length === 42 && (
            <Text variant="p2" tag="p">
              <a href={`https://polygonscan.com/address/${externalWalletAddr}`} target="_blank" rel="noreferrer">
                {externalWalletAddr.slice(0, 8)}...{externalWalletAddr.slice(-6)}
              </a>
            </Text>
          )}
          {/* NOTE: remove on Coinbase wallet support added */}
        </div>
        <div
          className={`mb-8 mt-10 flex h-[40px] flex-col items-center justify-center text-button-lg uppercase ${
            !isExternalWalletConnected && 'opacity-50'
          }`}
        >
          <div className="font-tactic-extended font-normal">External Wallet</div>
          {!isExternalWalletConnected && '(Not connected)'}
        </div>

        {nfts.length > 0 ? (
          nfts.map((nft) => (
            <div
              key={`Token ${nft.tokenId}`}
              className={`flex h-[50px] w-full items-center border px-6 ${
                nft.currentWallet !== 'externalwallet' && 'bg-[#151515]'
              }`}
            >
              {nft.currentWallet === 'externalwallet' && (
                <div className="flex w-full items-center">
                  <div
                    className={`mr-6 ${nft.transferAvailable ? 'cursor-pointer' : 'opacity-25'}`}
                    onClick={() => handleOpenConfirmModal(nft)}
                  >
                    {isExternalWalletLoading && confirmNFT?.name === nft.name ? (
                      <div className={styles.loader} />
                    ) : (
                      <TransferIcon type="left" />
                    )}
                  </div>
                  <div className="ml-2 font-tactic-extended text-button-lg font-normal uppercase">{nft.name}</div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="flex h-[50px] w-full items-center border bg-[#151515] px-6">
            <div className="font-tactic-extended text-button-lg font-normal uppercase opacity-50">
              no items in wallet
            </div>
          </div>
        )}
      </div>

      <Transition appear show={isConfirmModalOpen.isOpen} as={React.Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => setIsConfirmModalOpen({ isOpen: false, currentWallet: '' })}
        >
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-xl font-medium leading-6 text-gray-900">
                    Confirm Transfer
                  </Dialog.Title>
                  {!securityKeyApproved && confirmNFT?.currentWallet === 'mswallet' ? (
                    <>
                      <p className="text-sm text-gray-500">
                        <div className="mb-3 mt-2 italic">High value action protection</div>
                        First 2FA by clicking{' '}
                        <a href="#" onClick={handleAddKey} className="font-bold hover:underline">
                          here
                        </a>{' '}
                        {securityKeyAvailable ? <>to verify your passkey</> : <>to add a passkey with your device</>}
                      </p>
                      {!securityKeyAvailable ? (
                        <p className="pt-2.5 text-sm text-gray-500">
                          Note: To help ensure long term and secure access to your device passkey, we recommend “Use
                          another device“ on seeing the Windows Security popup after clicking above. As you can more
                          easily transfer your passkey to any future smartphone of yours. But, there’s known issue with
                          some Androids timing out too quickly on subsequent QR code scanning screen. If you run into
                          this issue, then we recommend just using PIN with Windows Hello on the same PC you play
                          DEADROP
                        </p>
                      ) : (
                        <>
                          <p className="py-2.5 text-sm text-gray-500">
                            If you want to revoke your passkey so you can add a new one on another device, click{' '}
                            <a href="#" onClick={handleRevokeKey} className="font-bold hover:underline">
                              here
                            </a>
                          </p>
                          <p className="text-sm text-gray-500">
                            If you lost access to your existing passkey and device,{' '}
                            <a
                              href="https://discord.gg/midnight-society"
                              target="_blank"
                              className="font-bold hover:underline"
                              rel="noreferrer"
                            >
                              #create-a-ticket on Discord
                            </a>
                          </p>
                        </>
                      )}
                      <div className="mt-2 flex flex-col items-center">
                        <Button
                          variant="primary"
                          size="small"
                          onClick={() => setIsConfirmModalOpen({ isOpen: false, currentWallet: '' })}
                        >
                          Close
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to transfer NFT to your{' '}
                          {isConfirmModalOpen.currentWallet === 'mswallet' ? 'connected external wallet' : 'MS wallet'}?
                        </p>
                      </div>

                      <div className="mt-4 flex justify-center">
                        <Button
                          variant="primary"
                          size="small"
                          onClick={isTransferLoading ? () => {} : handleTransfer}
                          state={isTransferLoading ? 'disabled' : ''}
                          className="mr-2 outline-none"
                        >
                          {isTransferLoading ? 'Transferring...' : 'Confirm'}
                        </Button>
                        <Button
                          variant="primary"
                          size="small"
                          onClick={() => setIsConfirmModalOpen({ isOpen: false, currentWallet: '' })}
                          className="outline-none"
                        >
                          Close
                        </Button>
                      </div>
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}
